import isEqual from 'lodash/isEqual';
import { useRef } from 'react';

import Text from 'components/common/Text';
import NutritionTableCaption from 'components/mealDetails/nutritionFacts/NutritionTableCaption';
import NutritionTableContentRows from 'components/mealDetails/nutritionFacts/NutritionTableContentRows';
import NutritionTableFooter from 'components/mealDetails/nutritionFacts/NutritionTableFooter';
import NutritionTableHeadingRows from 'components/mealDetails/nutritionFacts/NutritionTableHeadingRows';
import { useScrollToOffset } from 'hooks/useScrollToOffset';
import { TEMPO } from 'lib/constants/index';
import * as Types from 'utilities/types';

const DietaryList = ({ label, items }) => {
  const formatted = items.map(item => (typeof item === 'string' ? item : item.name)).join(', ');

  return (
    <Text className="mx-auto -mt-4 mb-8 max-w-[450px] px-6 uppercase bp1:px-0">
      <span className="font-bold">{label}: </span>
      <span className={label === 'CONTAINS' ? 'font-bold' : ''}>{formatted}.</span>
    </Text>
  );
};

DietaryList.propTypes = {
  label: PropTypes.string,
  items: PropTypes.array,
};

const NutritionFacts = ({
  meal: {
    displayUnit,
    menuCategory,
    menuCategoryForMeal,
    servingSize,
    mealPlanSlug,
    mealPlan,
    nutritionFacts: {
      servingSizeNetWeight,
      dailyValueAsPrepared,
      dailyPercentAsPrepared,
      dailyValueAsPackaged,
      dailyPercentAsPackaged,
    },
  },
}) => {
  const nutritionFactsContainerRef = useRef(null);
  const mealPlanName = mealPlanSlug || mealPlan;
  const isTempo = mealPlanName === TEMPO;

  const noPreparationDifference =
    isTempo ||
    (isEqual(dailyValueAsPrepared, dailyValueAsPackaged) &&
      isEqual(dailyPercentAsPrepared, dailyPercentAsPackaged));
  const needsAllNutritionDetails = !noPreparationDifference;

  return (
    <figure
      className="helvetica mx-auto my-6 w-[calc(100%-8px)] min-w-[320px] max-w-[450px] border-2 border-solid border-black-100 px-[0.2em] py-[0.5em] text-[90%] font-normal text-black-100 bp1:text-[100%]"
      ref={nutritionFactsContainerRef}
    >
      <table className="mx-0 mb-2 mt-0 w-full table-fixed border-x-0 border-b-[6px] border-t-2 border-solid border-black-100 text-[0.875em]">
        <NutritionTableCaption
          displayUnit={displayUnit}
          servingSizeNetWeight={servingSizeNetWeight}
          servingSize={servingSize}
          menuCategory={menuCategory}
          menuCategoryForMeal={menuCategoryForMeal}
          isTempo={isTempo}
          nutritionFactsContainerRef={nutritionFactsContainerRef}
        />
        <NutritionTableHeadingRows
          isTempo={isTempo}
          needsAllNutritionDetails={needsAllNutritionDetails}
          dailyValueAsPackaged={dailyValueAsPackaged}
          dailyValueAsPrepared={dailyValueAsPrepared}
          menuCategory={menuCategory}
        />
        <NutritionTableContentRows
          needsAllNutritionDetails={needsAllNutritionDetails}
          dailyPercentAsPackaged={dailyPercentAsPackaged}
          dailyPercentAsPrepared={dailyPercentAsPrepared}
          dailyValueAsPackaged={dailyValueAsPackaged}
          dailyValueAsPrepared={dailyValueAsPrepared}
          isTempo={isTempo}
        />
      </table>
      <NutritionTableFooter needsAllNutritionDetails={needsAllNutritionDetails} />
    </figure>
  );
};

NutritionFacts.propTypes = {
  meal: Types.meal.isRequired,
};

const NutritionFactsPanel = ({
  meal,
  hideIngredientDisclaimer = false,
  useScrollToTop = false,
}) => {
  const ref = useRef();

  useScrollToOffset({ ref, trigger: useScrollToTop, offset: 60 });

  return (
    <section ref={ref}>
      <header className="px-6 pb-2 pt-4">
        <h1 className="h2" id="meal-nutritional-facts" data-testid="meal-nutritional-facts">
          {meal.title}
        </h1>
        <p className="h5 mb-4 text-charcoal-90">{meal.subtitle}</p>
      </header>
      <div className="p-4">
        {meal.supplementNfpPhoto ? (
          <div className="mb-8 flex justify-center">
            <img
              src={meal.supplementNfpPhoto}
              alt={`Nutrition Facts Panel for ${meal.title}`}
              className="w-full max-w-[450px]"
            />
          </div>
        ) : (
          <NutritionFacts meal={meal} />
        )}
        <DietaryList label="INGREDIENTS" items={meal.ingredients} />
        <DietaryList label="CONTAINS" items={meal.allergens} />
        {!hideIngredientDisclaimer && (
          <Text className="helvetica pt-2 text-sm text-charcoal-90">
            Due to our just-in-time sourcing model, we may occasionally need to substitute
            ingredients. See packaging for final details.
          </Text>
        )}
      </div>
    </section>
  );
};

NutritionFactsPanel.propTypes = {
  meal: Types.meal.isRequired,
  hideIngredientDisclaimer: PropTypes.bool,
  useScrollToTop: PropTypes.bool,
};

export default NutritionFactsPanel;
